/**
 * T-173 (P1): the stale-chunk crash as a CLASS, not the /login instance.
 *
 * WHY T-137 DID NOT ALREADY COVER THIS, which is the whole finding:
 * lib/stale-chunk.ts is correct and stays. But it recovers by listening to
 * `window.onerror` and `unhandledrejection` — and a failed `React.lazy()`
 * import never reaches either of them. React CATCHES the rejection from a lazy
 * component and re-throws it during RENDER, where it is delivered to the
 * nearest error boundary. With no boundary in the tree it hits the router's
 * dev-facing wall ("Unexpected Application Error!"), which is exactly what the
 * operator saw when he clicked Join.
 *
 * So the global listeners fire for a stale chunk imported by a plain dynamic
 * `import()`, and NOT for the ~40 lazy ROUTES — which is every navigation in
 * this app. T-137 fixed the path nobody walks and left the path everybody
 * walks. Same shape as the byline: the instance was fixed, the class was
 * recorded as fixed, and the mechanism was untouched.
 *
 * WHAT THIS DOES: catches a render-time error, and if it carries the stale
 * chunk signature, reloads ONCE to fetch the current build. Any other error
 * re-renders as a visible fallback and is NOT reloaded away — decision 99, a
 * blanket "reload on any error" hides real bugs behind a refresh.
 *
 * THE LOOP IS THE THING THAT MUST NOT HAPPEN. A reload loop on the
 * registration page is worse than the crash: the user cannot even read the
 * error, and every cycle is another request. The one-shot guard lives in
 * sessionStorage and is SET BEFORE the reload (see recoverFromStaleChunk), so
 * if the fresh document still fails, the second failure renders visibly
 * instead of reloading again. That guard is shared with T-137's listeners, so
 * a page cannot get two reloads by failing through two different paths.
 */
import { Component, type ErrorInfo, type ReactNode } from 'react';
import { isStaleChunkError, recoverFromStaleChunk } from '@/lib/stale-chunk';

interface Props {
  children: ReactNode;
}

interface State {
  /** Set only for an error we are NOT recovering from automatically. */
  error: Error | null;
  /** True while a reload is in flight, so we render nothing rather than a flash. */
  reloading: boolean;
}

export class StaleChunkBoundary extends Component<Props, State> {
  state: State = { error: null, reloading: false };

  static getDerivedStateFromError(error: Error): Partial<State> {
    // A stale chunk is recoverable: suppress the fallback so the user never
    // sees an error frame before the reload we are about to trigger in
    // componentDidCatch. If the reload turns out to be unavailable (guard
    // already spent), componentDidCatch promotes it to a real error.
    if (isStaleChunkError(error)) return { reloading: true };
    return { error };
  }

  componentDidCatch(error: Error, _info: ErrorInfo): void {
    if (!isStaleChunkError(error)) return;

    const reloaded =
      typeof window !== 'undefined' &&
      recoverFromStaleChunk(error, window.sessionStorage, () => window.location.reload());

    // Guard already spent (or storage unavailable): this tab has had its one
    // automatic recovery and the build is still broken. Stop trying and show
    // the user something honest and actionable rather than looping.
    if (!reloaded) this.setState({ error, reloading: false });
  }

  render() {
    if (this.state.reloading) return null;

    if (this.state.error) {
      const stale = isStaleChunkError(this.state.error);
      return (
        <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-6 text-center">
          <h1 className="text-lg font-semibold text-text-primary">
            {stale ? 'This page is out of date' : 'Something went wrong'}
          </h1>
          <p className="max-w-md text-sm text-text-tertiary">
            {stale
              ? 'MotoWalla updated while this tab was open, and refreshing did not pick up the new version. Reloading manually should fix it.'
              : 'We hit an unexpected error loading this page.'}
          </p>
          <button
            type="button"
            onClick={() => window.location.reload()}
            className="rounded-xl bg-primary-500 px-5 py-2.5 font-semibold text-white transition-colors hover:bg-primary-600"
          >
            Reload the page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}
